import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, catchError, finalize, firstValueFrom, throwError } from "rxjs";
import { map, tap } from "rxjs/operators";
import { GetKeyDerivationsResponse } from "src/app/api/models/RipSim/HsmAdapter/Api/Features/GetKeyDerivation/Dto/get-key-derivations-response";
import { AppState } from "src/app/ngrx/app.reducer";
import { KeyDerivationEntity } from "../../../../src/app/api/models/key-derivation-entity";
import { AppActions } from "../../../../src/app/ngrx/app.actions.types";
import { LazyLoadingService } from "../../../../src/app/shared/services/lazy-loading.service";
import { LogService } from "../../../../src/app/shared/services/log.service";
import {
  ApiV2KeyDerivationsDelete$Params,
  ApiV2KeyDerivationsGet$Params,
  ApiV2KeyDerivationsPost$Params,
  apiV2KeyDerivationsDelete,
  apiV2KeyDerivationsGet,
  apiV2KeyDerivationsPost
} from "../fn";

export interface PaginatedKeyDerivationsResponse {
  profiles?: Array<KeyDerivationEntity> | null;
  total?: number | null;
}

@Injectable({
  providedIn: "root"
})
export class KeyDerivationsService extends LazyLoadingService<Array<KeyDerivationEntity>> {
  private _rootUrl = "";

  constructor(
    private store: Store<AppState>,
    private logService: LogService,
    private readonly http: HttpClient
  ) {
    super({ data: [] });
  }

  public get rootUrl(): string {
    return this._rootUrl;
  }

  public set rootUrl(value: string) {
    this._rootUrl = value;
  }

  public getKeyDerivations(params?: ApiV2KeyDerivationsGet$Params): Observable<GetKeyDerivationsResponse> {
    return apiV2KeyDerivationsGet(this.http, this.rootUrl, params).pipe(map((response) => response.body));
  }

  protected fetchData(params?: ApiV2KeyDerivationsGet$Params): Observable<Array<KeyDerivationEntity>> {
    this.logService.log("KeyDerivationsService::fetchData", JSON.stringify(params));
    return this.getKeyDerivations(params).pipe(
      map((response) => (response.results as Array<KeyDerivationEntity> | null | undefined) ?? [])
    );
  }

  public async mutate(payload: ApiV2KeyDerivationsPost$Params) {
    this.logService.log("KeyDerivationsService::apiV2KeyDerivationsPost", JSON.stringify(payload));
    await firstValueFrom(
      apiV2KeyDerivationsPost(this.http, this.rootUrl, payload).pipe(
        map((response) => response.body),
        tap(() =>
          this.store.dispatch(
            AppActions.ShowToastMessage({
              severity: "success",
              detail: $localize`Key Derivation successfully created.`
            })
          )
        ),
        catchError((err) => {
          if (err?.status !== 422) {
            this.store.dispatch(
              AppActions.ShowToastMessage({
                severity: "error",
                detail: $localize`Error uploading Key Derivation.`
              })
            );
          }
          return throwError(() => err);
        }),
        finalize(() => this.refresh())
      )
    );
  }

  public getKeyDerivationsPage(params: ApiV2KeyDerivationsGet$Params): Observable<PaginatedKeyDerivationsResponse> {
    return apiV2KeyDerivationsGet(this.http, this.rootUrl, params).pipe(
      map((response) => this.normalizePageResponse(response.body as unknown, params))
    );
  }

  public deleteKeyDerivationKeys(params: ApiV2KeyDerivationsDelete$Params, context?: HttpContext): Observable<void> {
    return apiV2KeyDerivationsDelete(this.http, this.rootUrl, params, context).pipe(map((response) => response.body));
  }

  private normalizePageResponse(body: unknown, _params: ApiV2KeyDerivationsGet$Params): PaginatedKeyDerivationsResponse {
    if (!body || typeof body !== "object") {
      return { profiles: [], total: 0 };
    }

    const paginatedResponse = body as GetKeyDerivationsResponse;
    return {
      profiles: (paginatedResponse.results ?? []).map((item) => ({
        ...item,
        keyIndex: item.keyIndex ?? 0,
        description: item.description ?? null
      })),
      total: paginatedResponse.totalResults ?? 0
    };
  }
}
